import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  Grid,
  useTheme,
  Divider,
  Alert,
  Chip,
  Snackbar
} from '@mui/material'
// import { PayPalButtons } from '@paypal/react-paypal-js' // Removed - using backend approach
import {
  CheckCircle as CheckIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

// Plans will be fetched from API

const steps = [
  {
    label: 'Plan Summary',
    description: 'Review your selected plan and features',
    icon: CheckIcon
  }
]

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
}

const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
}

const Payment = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(0)
  const [email, setEmail] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isValidating, setIsValidating] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  })
  const [plans, setPlans] = useState({})
  const [plansLoading, setPlansLoading] = useState(true)
  const [userInfo, setUserInfo] = useState(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [paypalLoaded, setPaypalLoaded] = useState(false)
  
  // Get plan and token from URL params
  const urlParams = new URLSearchParams(window.location.search)
  const selectedPlanId = urlParams.get('plan') || ''
  const selectedToken = urlParams.get('token') || ''
  const selectedPhone = urlParams.get('phone') || ''
  const selectedPlan = plans[selectedPlanId]
  

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/plans`)
        if (response.ok) {
          const data = await response.json()
          // Handle different response formats
          let plansArray = [];
          
          if (data.plans && Array.isArray(data.plans)) {
            // Format: { success: true, plans: [...] }
            plansArray = data.plans;
          } else if (data.data && Array.isArray(data.data)) {
            // Format: { success: true, data: [...] }
            plansArray = data.data;
          } else if (Array.isArray(data)) {
            // Format: [...] (direct array)
            plansArray = data;
          }
          
          console.log('Payment page plans array:', plansArray);
          
          // Transform plans to object format for easy lookup
          const plansObject = {}
          plansArray.forEach(plan => {
            plansObject[plan.id] = {
              name: plan.name,
              price: plan.price,
              period: 'month',
              reminders: plan.maxReminders,
              totalMessages: plan.maxReminders * 10,
              color: plan.color
            }
          })
          console.log('Payment page plans object:', plansObject);
          setPlans(plansObject)
        } else {
          console.error('Failed to fetch plans')
        }
      } catch (error) {
        console.error('Error fetching plans:', error)
      } finally {
        setPlansLoading(false)
      }
    }

    fetchPlans()
  }, [])

  // Validation and redirect logic
  useEffect(() => {
    if (plansLoading) return // Wait for plans to load
    
    const verifyTokenAndValidate = async () => {
      console.log('Payment page validation:', { selectedPlanId, selectedToken, selectedPhone, selectedPlan })
      let errors = []
      
      if (!selectedPlanId || selectedPlanId.trim() === '') {
        errors.push('🔴 Plan selection is missing')
      }
      
      if (!selectedToken || selectedToken.trim() === '') {
        errors.push('🔑 Authentication token is missing')
      }
      
      if (selectedPlanId && !selectedPlan) {
        errors.push('❌ Invalid plan selected')
      }
      
      if (errors.length > 0) {
        // Show error message
        const errorMessage = `⚠️ Payment Error: ${errors.join(', ')}`
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        })
        
        // Redirect to membership page after 3 seconds
        setTimeout(() => {
          navigate('/membership')
        }, 6000)
        
        setIsValidating(false)
        return false
      }

      // Verify token with backend (same as membership page)
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/validate-token/${selectedToken}`)
        const data = await response.json()
        
        if (!data.success) {
          throw new Error(data.message || 'Token verification failed')
        }
        
        // Store user info from token verification
        setUserInfo(data.data)
        console.log('User info stored:', data.data)
        
        // Pre-fill email if available
        if (data.data.email) {
          setEmail(data.data.email)
        }
        
        setIsValidating(false)
        return true
      } catch (error) {
        setSnackbar({
          open: true,
          message: `⚠️ Authentication Error: ${error.message}`,
          severity: 'error'
        })
        
        // Redirect to membership page after 3 seconds
        setTimeout(() => {
          navigate('/membership')
        }, 6000)
        
        setIsValidating(false)
        return false
      }
    }
    
    verifyTokenAndValidate()
  }, [selectedPlanId, selectedToken, selectedPhone, selectedPlan, navigate, plansLoading])
  
  // Show loading while validating or loading plans
  if (isValidating || plansLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box textAlign="center">
          <Typography variant="h4" gutterBottom>
            {isValidating ? 'Validating...' : 'Loading Plans...'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we process your request.
          </Typography>
        </Box>
      </Container>
    )
  }

  // Don't render if validation failed
  if (!selectedPlan || !selectedPlanId || !selectedToken) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              404 - Page Not Found
            </Typography>
            <Typography>
              Required parameters (plan and token) are missing. You will be redirected shortly.
            </Typography>
          </Alert>
        </Box>
      </Container>
    )
  }

  const handleNext = async () => {
    // On Continue: call backend to upgrade plan using phone, token, plan, price
    try {
      const payload = {
        phoneNumber: userInfo?.phoneNumber || selectedPhone,
        accessToken: selectedToken,
        planType: (selectedPlanId || '').toUpperCase(),
        price: selectedPlan?.price,
        reminderLimit: selectedPlan?.reminders
      }
      console.log('Continue clicked. Sending payload to /api/upgrade-plan:', payload)
      const resp = await fetch(`${import.meta.env.VITE_API_BASE_URL}/upgrade-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const data = await resp.json()
      console.log('Response from /api/upgrade-plan:', { status: resp.status, data })
      if (!resp.ok || !data.success) {
        throw new Error(data.message || 'Failed to upgrade plan')
      }
      setSnackbar({ open: true, message: 'Plan activated successfully', severity: 'success' })
      setPaymentSuccess(true)
      // Redirect or show success
      navigate('/admin')
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' })
    }
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  const handleBack = () => {
    navigate(-1)
  }

  // PayPal payment handler using backend
  const handlePayPalPayment = async () => {
    if (!selectedPlan) return;
    
    setIsProcessing(true);
    setPaymentError(null);
    
    try {
      // Prepare request data with proper data types
      const price = parseFloat(selectedPlan.price);
      
      // Validate that price conversion was successful
      if (isNaN(price)) {
        throw new Error(`Invalid plan data: price=${selectedPlan.price} (${typeof selectedPlan.price})`);
      }
      
      const requestData = {
        planType: selectedPlan.name,
        price: price,
        phoneNumber: userInfo?.phoneNumber,
        accessToken: userInfo?.accessToken
      };
      
      // Debug logs removed for production
      
      // Call your backend to create PayPal order
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/upgrade-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      
      if (data.success && data.approveUrl) {
        // Redirect user to PayPal approval URL
        window.location.href = data.approveUrl;
      } else {
        // Log detailed error information
        console.error('API Error:', data);

        const errorMessage = data.errors ? 
          `Validation failed: ${data.errors.map(e => e.msg).join(', ')}` : 
          data.message || 'Failed to create PayPal order';
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.message || 'Payment failed. Please try again.');
      
      setSnackbar({
        open: true,
        message: error.message || 'Payment failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayment = () => {
    if (selectedPlan.price === 0) {
      // Free plan - no payment needed
      setIsProcessing(true);
      setTimeout(() => {
        setIsProcessing(false);
        handleNext();
      }, 1000);
    } else {
      // Paid plan - use PayPal backend integration
      handlePayPalPayment();
    }
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Plan Details
            </Typography>
            
            {/* User Information Display */}
            
            {/* Phone Number and Token Display */}
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">
                  <strong>📱 Phone Number:</strong>
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {userInfo?.phoneNumber || selectedPhone || 'Not provided'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">
                  <strong>🔑 Token:</strong>
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all'
                }}>
                  {selectedToken}
                </Typography>
              </Box>
            </Alert>
            <Card
              sx={{
                border: `2px solid ${selectedPlan.color}`,
                background: `linear-gradient(135deg, ${selectedPlan.color}08, ${selectedPlan.color}15)`
              }}
            >
              <CardContent>
                <Box textAlign="center" mb={2}>
                  <Typography variant="h4" component="h3" gutterBottom fontWeight={600}>
                    {selectedPlan.name} Plan
                  </Typography>
                  <Typography
                    variant="h3"
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: selectedPlan.color
                    }}
                  >
                    ${selectedPlan.price}
                  </Typography>
                  <Typography
                    variant="h6"
                    component="span"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    /{selectedPlan.period}
                  </Typography>
                </Box>
                
                <Grid container spacing={2} textAlign="center" mb={3}>
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight={600} color={selectedPlan.color}>
                      {selectedPlan.reminders}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Reminders per month
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight={600} color={selectedPlan.color}>
                      {selectedPlan.totalMessages}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total messages
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Your plan includes:
                </Typography>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckIcon sx={{ color: selectedPlan.color, fontSize: 20, mr: 1.5 }} />
                    <Typography variant="body2">WhatsApp integration</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckIcon sx={{ color: selectedPlan.color, fontSize: 20, mr: 1.5 }} />
                    <Typography variant="body2">Smart scheduling</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckIcon sx={{ color: selectedPlan.color, fontSize: 20, mr: 1.5 }} />
                    <Typography variant="body2">Analytics dashboard</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckIcon sx={{ color: selectedPlan.color, fontSize: 20, mr: 1.5 }} />
                    <Typography variant="body2">7-day free trial</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )
      
      // Removed Payment Details and Confirmation steps
      
      default:
        return 'Unknown step'
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: 4
      }}
    >
      <Container maxWidth="md">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <Box textAlign="center" mb={6}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Complete Your Purchase
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
              >
                Secure payment via PayPal - Get instant access to your new plan
              </Typography>
            </motion.div>
          </Box>

          {/* Stepper */}
          <motion.div variants={cardVariants}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((step, index) => {
                    const IconComponent = step.icon
                    return (
                      <Step key={step.label}>
                        <StepLabel
                          StepIconComponent={() => (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: index <= activeStep 
                                  ? theme.palette.primary.main 
                                  : theme.palette.divider,
                                color: 'white'
                              }}
                            >
                              <IconComponent sx={{ fontSize: 18 }} />
                            </Box>
                          )}
                        >
                          <Typography variant="h6" fontWeight={600}>
                            {step.label}
                          </Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography color="text.secondary" sx={{ mb: 2 }}>
                            {step.description}
                          </Typography>
                          {getStepContent(index)}
                          <Box sx={{ mt: 2 }}>
                            <Button
                              variant="contained"
                              onClick={handlePayment}
                              disabled={isProcessing}
                              sx={{ mr: 1 }}
                            >
                              {isProcessing ? 'Processing...' : 'Complete Payment'}
                            </Button>
                            <Button
                              disabled={false}
                              onClick={handleBack}
                              sx={{ ml: 1 }}
                            >
                              Back
                            </Button>
                          </Box>
                        </StepContent>
                      </Step>
                    )
                  })}
                </Stepper>
              </CardContent>
            </Card>
          </motion.div>

          {/* Footer */}
          <Box textAlign="center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Typography variant="body2" color="text.disabled">
                Need help? Contact our support team
              </Typography>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
      
      {/* Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Payment
